import React, { useEffect, useState } from 'react';
import { syncPrefsAtStartup } from "../helpers/prefs_api";
import CommandPalette from '../Components/Common/CommandPalette';
import { PageHeaderTrailProvider } from '../Components/Common/DashboardKit/PageHeaderTrailContext';
import PropTypes from "prop-types";
import withRouter from '../Components/Common/withRouter';

import { useLocation } from "react-router-dom";
import { routeMeta } from "../utils/routeMeta";
import { getPageMeta } from "../utils/getPageMeta";

//import Components
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import RightSidebar from '../Components/Common/RightSidebar';
import PendingApprovalsBanner from '../Components/Common/PendingApprovalsBanner';
import { Splash3D, RouteLoader } from '../Components/Common/Logo3D';

//import actions
import {
    changeLayout,
    changeSidebarTheme,
    changeLayoutMode,
    changeLayoutWidth,
    changeLayoutPosition,
    changeTopbarTheme,
    changeLeftsidebarSizeType,
    changeLeftsidebarViewType,
    changeSidebarImageType,
    changeSidebarVisibility
} from "../slices/thunks";

//redux
import { useSelector, useDispatch, shallowEqual } from "react-redux";


const Layout = (props) => {
    // Round 40 — pull server-side user preferences once per app load and
    // seed localStorage/DOM (silent no-op until the API is deployed).
    React.useEffect(() => { syncPrefsAtStartup(); }, []);

    // Brand splash: show the spinning combined 3D logo on every full page load
    // / refresh. The Layout component mounts once per full page load (client-
    // side route changes don't remount it), so a simple mount-time `true` shows
    // the splash on each refresh but NOT on in-app navigation (RouteLoader
    // handles those). No sessionStorage gate — a refresh should show it again.
    const [showSplash, setShowSplash] = useState(true);
    const dismissSplash = React.useCallback(() => {
        setShowSplash(false);
    }, []);

    const dispatch = useDispatch();
    const {
        layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype
    } = useSelector(state => ({
        layoutType: state.Layout.layoutType,
        leftSidebarType: state.Layout.leftSidebarType,
        layoutModeType: state.Layout.layoutModeType,
        layoutWidthType: state.Layout.layoutWidthType,
        layoutPositionType: state.Layout.layoutPositionType,
        topbarThemeType: state.Layout.topbarThemeType,
        leftsidbarSizeType: state.Layout.leftsidbarSizeType,
        leftSidebarViewType: state.Layout.leftSidebarViewType,
        leftSidebarImageType: state.Layout.leftSidebarImageType,
        sidebarVisibilitytype: state.Layout.sidebarVisibilitytype
    }), shallowEqual);

    /*
    layout settings
    */
    useEffect(() => {
        if (
            layoutType ||
            leftSidebarType ||
            layoutModeType ||
            layoutWidthType ||
            layoutPositionType ||
            topbarThemeType ||
            leftsidbarSizeType ||
            leftSidebarViewType ||
            leftSidebarImageType ||
            sidebarVisibilitytype
        ) {
            window.dispatchEvent(new Event('resize'));
            dispatch(changeLeftsidebarViewType(leftSidebarViewType));
            dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
            dispatch(changeSidebarTheme(leftSidebarType));
            dispatch(changeLayoutMode(layoutModeType));
            dispatch(changeLayoutWidth(layoutWidthType));
            dispatch(changeLayoutPosition(layoutPositionType));
            dispatch(changeTopbarTheme(topbarThemeType));
            dispatch(changeLayout(layoutType));
            dispatch(changeSidebarImageType(leftSidebarImageType));
            dispatch(changeSidebarVisibility(sidebarVisibilitytype));
        }
    }, [layoutType,
        leftSidebarType,
        layoutModeType,
        layoutWidthType,
        layoutPositionType,
        topbarThemeType,
        leftsidbarSizeType,
        leftSidebarViewType,
        leftSidebarImageType,
        sidebarVisibilitytype,
        dispatch]);
    /*
    call dark/light mode
    */
    const onChangeLayoutMode = (value) => {
        if (changeLayoutMode) {
            dispatch(changeLayoutMode(value));
        }
    };

    const [headerClass, setHeaderClass] = useState("");
    // class add remove in header
    //
    // ⚠ CRITICAL: this useEffect originally had NO dependency array, which
    // caused a new scroll listener to be attached on every render. Combined
    // with setHeaderClass firing on every scroll event, this led to hundreds
    // of accumulated listeners and the "page blip when scrolling" symptom
    // (scrollbar would flicker because the page re-rendered many times per
    // scroll tick, and the user could only scroll by clicking the scrollbar
    // track directly). Fixed by registering exactly once with proper cleanup.
    useEffect(() => {
        function scrollNavigation() {
            const scrollup = document.documentElement.scrollTop;
            if (scrollup > 50) {
                setHeaderClass("topbar-shadow");
            } else {
                setHeaderClass("");
            }
        }
        window.addEventListener("scroll", scrollNavigation, true);
        return () => {
            window.removeEventListener("scroll", scrollNavigation, true);
        };
    }, []);

    useEffect(() => {
        const hamburger = document.querySelector(".hamburger-icon");
        if (!hamburger) return;

        if (
            sidebarVisibilitytype === "show" ||
            layoutType === "vertical" ||
            layoutType === "twocolumn"
        ) {
            hamburger.classList.remove("open");
        } else {
            hamburger.classList.add("open");
        }
    }, [sidebarVisibilitytype, layoutType]);
    const location = useLocation();

    const [pageMeta, setPageMeta] = useState({
        title: "",
        pageTitle: ""
    });

    useEffect(() => {
        const meta = getPageMeta(location.pathname, routeMeta);
        setPageMeta(meta);
    }, [location.pathname]);

    // Go-live parity (2026-09-24): the legacy display boards ran full-screen
    // on the shop-floor TVs with no application chrome. /display-boards?kiosk=1
    // renders the board page alone (no header, sidebar, footer, banner or
    // splash). Sign-in and the page's own permission gate still apply.
    const isBoardKiosk =
        location.pathname.startsWith("/display-boards") &&
        new URLSearchParams(location.search).get("kiosk") === "1";
    if (isBoardKiosk) {
        return (
            <React.Fragment>
                <PageHeaderTrailProvider>
                    {React.Children.map(props.children, (child) =>
                        React.isValidElement(child)
                            ? React.cloneElement(child, { setPageMeta })
                            : child
                    )}
                </PageHeaderTrailProvider>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {showSplash && <Splash3D minDurationMs={1600} onDone={dismissSplash} />}
            <RouteLoader />
            <PageHeaderTrailProvider>
            <CommandPalette />
            <div id="layout-wrapper">
                <Header
                    headerClass={headerClass}
                    layoutModeType={layoutModeType}
                    onChangeLayoutMode={onChangeLayoutMode}
                    pageMeta={pageMeta}
                />

                <Sidebar layoutType={layoutType} />

                <div className="main-content">
                    {React.Children.map(props.children, (child) =>
                        React.isValidElement(child)
                            ? React.cloneElement(child, { setPageMeta })
                            : child
                    )}

                    <Footer />
                </div>
            </div>

            <RightSidebar />
            <PendingApprovalsBanner />
            </PageHeaderTrailProvider>
        </React.Fragment>
    );

};

Layout.propTypes = {
    children: PropTypes.object,
};

export default withRouter(Layout);